import { useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, ShieldCheck, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import SafeImage from '../../../components/SafeImage';
import { openExternalUrl } from '../../../lib/externalNavigation';
import {
  PLAYER_APK_RELEASE,
  getNativePlayerBridge,
  playerApkPath,
  shouldPromptPlayerApkUpdate,
} from '../../../lib/playerApk';

const absoluteUrl = (path) => {
  if (!path || typeof window === 'undefined') return '';
  return new URL(path, window.location.origin).href;
};

export default function PlayerApkUpdatePrompt({
  tenantSlug,
  brandLogo,
  brandName,
}) {
  const [open, setOpen] = useState(false);
  const [openingDownload, setOpeningDownload] = useState(false);
  const apkUrl = useMemo(
    () => absoluteUrl(playerApkPath(tenantSlug)),
    [tenantSlug]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setOpen(shouldPromptPlayerApkUpdate());
    }, 250);
    return () => window.clearTimeout(timer);
  }, [tenantSlug]);

  if (!open || !apkUrl) return null;

  const copyDownloadLink = async () => {
    try {
      await navigator.clipboard.writeText(apkUrl);
      toast.success('Download link copied', {
        description: 'Open Chrome, paste the link, and install the update.',
        duration: 5200,
      });
      return true;
    } catch {
      toast.error('Could not copy the download link', {
        description: apkUrl,
        duration: 8000,
      });
      return false;
    }
  };

  const startUpdate = async () => {
    setOpeningDownload(true);
    const bridge = getNativePlayerBridge();
    const hasNativeOpener = typeof bridge?.openExternalUrl === 'function';

    if (bridge && !hasNativeOpener) {
      let shared = false;
      if (typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: `${brandName || 'Player'} app update`,
            text: 'Open this download in Chrome to install the latest app.',
            url: apkUrl,
          });
          shared = true;
        } catch {
          shared = false;
        }
      }
      if (!shared) {
        await copyDownloadLink();
      }
      window.setTimeout(() => setOpeningDownload(false), 600);
      return;
    }

    const opened = openExternalUrl(apkUrl);
    if (opened) {
      toast.success('Update download opened', {
        description: 'Download the APK, then tap Install when Android asks.',
        duration: 4200,
      });
    } else {
      await copyDownloadLink();
    }
    window.setTimeout(() => setOpeningDownload(false), 1200);
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center overflow-y-auto overscroll-contain bg-black/75 px-3 pb-[max(14px,env(safe-area-inset-bottom))] pt-3 backdrop-blur-md sm:items-center sm:pt-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="player-apk-update-title"
      data-testid="player-apk-update-prompt"
    >
      <section
        className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-md flex-col overflow-hidden rounded-[26px] border"
        style={{
          background: 'linear-gradient(180deg, #13243A 0%, #08111E 100%)',
          borderColor: 'color-mix(in oklab, var(--vl-neon, #22FF88) 48%, rgba(255,255,255,0.16))',
          boxShadow: '0 28px 72px rgba(0,0,0,0.62), 0 0 36px color-mix(in oklab, var(--vl-neon, #22FF88) 20%, transparent)',
        }}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-white/10 px-4 py-3 min-[360px]:px-5 min-[360px]:py-4">
          <div
            className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-2xl border border-white/15 bg-black/30"
            aria-hidden
          >
            {brandLogo ? (
              <SafeImage
                src={brandLogo}
                alt=""
                className="h-full w-full object-cover"
                loading="eager"
                fallback={<Smartphone className="h-7 w-7 text-white" />}
              />
            ) : (
              <Smartphone className="h-7 w-7 text-white" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: 'var(--vl-neon, #22FF88)' }}>
              New Android version
            </p>
            <h2 id="player-apk-update-title" className="mt-1 text-xl font-extrabold leading-tight text-white">
              Update to the latest version
            </h2>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto overscroll-contain px-4 py-4 min-[360px]:px-5 min-[360px]:py-5">
          <p className="text-sm leading-relaxed text-white/72">
            A newer {brandName || 'player'} app is ready with the latest fixes, smoother uploads, and better reliability.
          </p>

          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-white/10 bg-black/25 p-3.5">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" style={{ color: '#FACC15' }} />
            <div>
              <p className="text-sm font-bold text-white">Version {PLAYER_APK_RELEASE}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-white/58">
                Your account and game information stay unchanged. Android will ask you to confirm the update.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={startUpdate}
            disabled={openingDownload}
            className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-sm font-black text-black transition active:scale-[0.98] disabled:cursor-wait disabled:opacity-80"
            style={{
              background: 'linear-gradient(135deg, var(--vl-neon, #22FF88), #FACC15)',
              boxShadow: '0 14px 30px color-mix(in oklab, var(--vl-neon, #22FF88) 24%, transparent)',
            }}
            data-testid="player-apk-update-button"
          >
            {openingDownload ? (
              <RefreshCw className="h-5 w-5 animate-spin" />
            ) : (
              <Download className="h-5 w-5" />
            )}
            Update now
          </button>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-2 h-11 w-full text-xs font-bold text-white/55 transition hover:text-white/80"
            data-testid="player-apk-update-later"
          >
            Remind me later
          </button>

          <button
            type="button"
            onClick={copyDownloadLink}
            className="h-9 w-full text-[11px] font-bold text-white/45 transition hover:text-white/75"
            data-testid="player-apk-copy-link"
          >
            Copy download link
          </button>
        </div>
      </section>
    </div>
  );
}
